import React, { useState } from 'react';
import Papa from 'papaparse';
import { addDocument, findDocumentsByField } from '../services/firestoreService';
import {
  Box, Button, Typography, Paper, Alert, LinearProgress, Input
} from '@mui/material';

const CsvImporter = ({ onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(0);

  const handleFileChange = (e) => {
    setError('');
    setSuccess('');
    setFile(e.target.files[0]);
  };

  const processImport = async () => {
    if (!file) {
      setError('Por favor, selecciona un archivo CSV.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const records = results.data;
        let importedCount = 0;
        let duplicateCount = 0;

        for (let i = 0; i < records.length; i++) {
          const record = records[i];
          try {
            // Helper robusto para encontrar un valor por el prefijo de la clave
            const getValueByPrefix = (prefix) => {
              const recordKeys = Object.keys(record);
              const key = recordKeys.find((k) => k.trim().startsWith(prefix));
              return key ? record[key] : '';
            };

            // ID único del formulario (clave para evitar duplicados)
            const formEntryId =
              getValueByPrefix('id') ||
              getValueByPrefix('form_id') ||
              getValueByPrefix('response_id') ||
              getValueByPrefix('submission_id');

            if (!formEntryId) {
              setError(`La fila ${i + 1} no tiene un ID de formulario. Asegúrate de incluir una columna de ID (por ejemplo, "id" o "form_id").`);
              setLoading(false);
              return;
            }

            // Verificar si ya existe una solicitud con este ID de formulario
            const existingRequests = await findDocumentsByField('contact-requests', 'formEntryId', formEntryId);
            if (existingRequests.length > 0) {
              // Duplicado: no volver a importar
              duplicateCount++;
              setProgress(Math.round(((i + 1) / records.length) * 100));
              continue;
            }

            // Campos del formulario usados para construir el mensaje original
            const formName = getValueByPrefix('form_name') || '';
            const needsSurgeryAnswer = getValueByPrefix('¿necesitas_realizar_una_cirugía_a_tu_mascota?');
            const surgeryTypeAnswer = getValueByPrefix('¿qué_tipo_de_cirugía_necesita_tu_mascota?');

            let additionalInfo = formName;
            const normalizedNeeds = String(needsSurgeryAnswer || '').trim().toLowerCase();
            const needsSurgery = normalizedNeeds === 'sí' || normalizedNeeds === 'si';

            if (needsSurgery) {
              const cleanSurgeryType = String(surgeryTypeAnswer || '').trim();
              if (cleanSurgeryType) {
                const suffix = `Necesito cirugía de ${cleanSurgeryType}`;
                additionalInfo = additionalInfo
                  ? `${additionalInfo} - ${suffix}`
                  : suffix;
              }
            }

            // 1. Mapear y crear objeto de propietario
            const cleanNumeric = (value) => String(value || '').replace(/[^0-9]/g, '');

            const rawCreatedTime = getValueByPrefix('created_time');
            let createdAt = new Date();
            if (rawCreatedTime) {
              const parsed = new Date(rawCreatedTime);
              if (!Number.isNaN(parsed.getTime())) {
                createdAt = parsed;
              }
            }

            const ownerData = {
              name: getValueByPrefix('full_name') || 'N/A',
              contact: getValueByPrefix('email'),
              celular: cleanNumeric(getValueByPrefix('phone_number')),
              rut: '',
              comuna: '',
              convenio: '',
            };
            const ownerId = await addDocument('owners', ownerData);

            // 2. Mapear y crear objeto de paciente
            const homologateSpecies = (species) => {
              const lowerCaseSpecies = String(species || '').toLowerCase();
              if (lowerCaseSpecies.includes('perro')) return 'Canino';
              if (lowerCaseSpecies.includes('gato')) return 'Felino';
              return species; // Devolver el original si no coincide
            };

            const patientData = {
              name: getValueByPrefix('nombre_de_tu_mascota') || 'N/A',
              species: homologateSpecies(getValueByPrefix('¿qué_especie_es_tu_mascota?')),
              genero: getValueByPrefix('¿qué_sexo_es_tu_mascota?'),
              edad: cleanNumeric(getValueByPrefix('edad_de_tu_mascota')),
              ownerId: ownerId,
              breed: '',
              birthDate: '',
            };
            const patientId = await addDocument('patients', patientData);

            // 3. Mapear y crear objeto de solicitud de contacto
            const requestData = {
              source: getValueByPrefix('platform') || 'CSV Import',
              ownerName: ownerData.name,
              patientName: patientData.name,
              contactInfo: ownerData.celular,
              attentionNeed: '', // Dejar en blanco para completar después
              patientType: 'Sin Convenio', // Valor fijo
              assignedTo: '',
              ownerId: ownerId,
              patientId: patientId,
              formName,
              additionalInfo,
              formEntryId,
              createdAt,
              status: 'Nuevo',
            };
            await addDocument('contact-requests', requestData);

            importedCount++;
          } catch (err) {
            console.error('Error procesando la fila:', record, err);
            setError(`Error al procesar la fila ${i + 1}. La importación se ha detenido.`);
            setLoading(false);
            return; // Detener en caso de error
          }
          setProgress(Math.round(((i + 1) / records.length) * 100));
        }

        const duplicateInfo = duplicateCount > 0
          ? ` (${duplicateCount} registros omitidos por ID de formulario duplicado)`
          : '';
        setSuccess(`${importedCount} de ${records.length} registros importados con éxito.${duplicateInfo}`);
        if (onImportComplete) onImportComplete();
        setLoading(false);
      },
      error: (err) => {
        setError('Error al leer el archivo CSV: ' + err.message);
        setLoading(false);
      },
    });
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>Importar Leads desde CSV</Typography>
      <Box>
        <Input type="file" accept=".csv" onChange={handleFileChange} sx={{ mb: 2 }} />
        <Button variant="contained" onClick={processImport} disabled={loading || !file}>
          Importar Archivo
        </Button>
      </Box>
      {loading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="text.secondary">{`${progress}%`}</Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
    </Paper>
  );
};

export default CsvImporter;
